<template>
  <v-dialog scrollable persistent :value="show" width="538px">
    <v-card class="px-6 py-4"
      ><v-card-title class="pa-0 ma-0 h6">{{
        $t('routines.create_finding')
      }}</v-card-title>
      <v-card-text class="pa-0 ma-0 mt-5">
        <v-text-field
          required
          outlined
          dense
          autofocus
          v-model="finding.situation"
          class="mt-1"
          :maxlength="situationMaxLength"
          :label="`${$t('routines.finding_one')}*`"
        />

        <AssigneesSelector
          multiple
          :class="{
            'mb-3':
              finding.assignees.length >
              $constants.routines.assignees.maxLength,
          }"
          :selectedAccountIds="finding.assignees"
          :maxSelectableAccounts="$constants.routines.assignees.maxLength"
          @change="finding.assignees = $event"
        />

        <TagsSelector
          class="mb-6"
          data-private
          data-testid="addFindingModal-selector-tags"
          :allowedTags="resolvedAvailableTagObjects"
          :required="isFindingTagsRequired"
          :siteId="siteId"
          :value="finding.tags"
          @change="handleTagsChange"
        />

        <CategoriesSelector
          class="mb-6"
          data-private
          v-model="finding.categories"
          :siteIds="siteIds"
        />

        <SituationTypesSelector
          v-if="isQuickAction && hasPresetSituationTypes"
          class="mb-6"
          :presetSituationTypes="presetSituationTypes"
          v-model="finding.situationTypes"
        />

        <DueDatePicker
          isEditMode
          :dueDateTime="finding.dueDate"
          @change="handleSetDueDate"
        />

        <PrioritySelector
          class="mb-6"
          :priority="findingPriority"
          @change="finding.priority = $event"
        />

        <p class="grey--text text--darken-1 font-size-12 ma-0 pa-0 mb-1">
          {{ $t('assignments.solution_evidence_label') }}
        </p>
        <MediaContainer
          ref="mediaContainer"
          onlyThumbnails
          allowMultipleSelect
          :images="finding.media"
          @change="handleMediaChange"
        />
      </v-card-text>
      <v-card-actions class="mb-0 mt-4 pa-0 justify-end">
        <Button
          plain
          color="grey"
          :text="$t('common.button.cancel')"
          @click="handleCancel"
        />
        <Button
          color="primary"
          :disabled="disableSaveButton"
          :loading="isLoading"
          :text="$t('common.button.save')"
          @click="handleSave"
        />
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>
<script>
import { mapGetters } from 'vuex'
import { utils } from 'frontend-js-shared'
import _ from 'lodash'

import api from '@/api'
import mixin from '@/mixins/mixin.vue'
import userMixin from '@/mixins/userMixin.vue'
import revMixin from '@/mixins/revMixin.vue'
import trackerMixin from '@/mixins/tracking/trackerMixin.vue'
import DueDatePicker from '@/components/DueDatePicker/DueDatePicker.vue'

import MediaContainer from '@/components/MediaContainer/MediaContainer.vue'
import AssigneesSelector from '@/components/AssigneesSelector/AssigneesSelector.vue'
import SituationTypesSelector from '@/components/SituationTypesSelector.vue'
import TagsSelector from '@/components/TagsSelector/TagsSelector.vue'
import PrioritySelector from '@/components/PrioritySelector/PrioritySelector.vue'
import CategoriesSelector from '@/components/CategoriesSelector.vue'
import Button from '@/components/UI/Button'

export default {
  name: 'AddFindingModal',

  mixins: [mixin, userMixin, revMixin, trackerMixin],

  props: {
    show: Boolean,
    routineDefinition: {
      type: Object,
      default() {
        return {}
      },
    },
    selectedTagIds: {
      type: Array,
      default() {
        return []
      },
    },
    situation: String,
    routine: { type: Object, required: true },
  },

  components: {
    MediaContainer,
    AssigneesSelector,
    DueDatePicker,
    SituationTypesSelector,
    TagsSelector,
    PrioritySelector,
    CategoriesSelector,
    Button,
  },

  data() {
    return {
      availableAccounts: [],
      isLoading: false,
      availableTagsForFindings: [],
      presetTags: null,
      presetSituationTypes: null,
      finding: {
        situation: this.situation,
        categories: [],
        tags: this.selectedTagIds.map(tagId => utils.getEntityId(tagId)),
        situationTypes: [],
        assignees: [],
        dueDate: null,
        media: [],
        revType: null,
      },
    }
  },

  async mounted() {
    await this.fetchRoutineDefinition()

    this.finding.revType = this.defaultRevType
  },

  computed: {
    ...mapGetters(['organizationId', 'organization', 'organizationTags']),

    disableSaveButton() {
      return (
        !this.finding.situation?.trim?.() ||
        (this.isFindingTagsRequired && _.isEmpty(this.finding.tags)) ||
        this.finding.assignees.length >
          this.$constants.routines.assignees.maxLength
      )
    },

    isFindingTagsRequired() {
      return Boolean(this.routineDefinition?.requireFindingTags)
    },

    isQuickAction() {
      return (
        this.finding.revType ===
        this.pascalCase(this.$constants.revTypes.quickAction)
      )
    },

    findingPriority() {
      return this.finding.priority ?? null
    },

    hasPresetTags() {
      return Boolean(this.presetTags?.length)
    },

    hasPresetSituationTypes() {
      return this.presetSituationTypes
    },

    resolvedAvailableTagsForFindings() {
      if (!_.isEmpty(this.routineDefinition?.availableTagsForFindings)) {
        return this.routineDefinition.availableTagsForFindings
      }

      return this.availableTagsForFindings
    },

    resolvedAvailableTagObjects() {
      const pool = this.organizationTags ?? []
      const allowedIds = this.resolvedAvailableTagsForFindings
      if (_.isEmpty(allowedIds)) return pool

      const allowedSet = new Set(allowedIds.map(tag => utils.getEntityId(tag)))
      return pool.filter(tag => allowedSet.has(utils.getEntityId(tag)))
    },

    defaultRevType() {
      if (this.organization?.featuresEnabled__quickAction) {
        return this.pascalCase(this.$constants.revTypes.quickAction)
      }

      if (this.organization?.featuresEnabled__improvementIdea) {
        return this.pascalCase(this.$constants.revTypes.improvementIdea)
      }

      return null
    },

    situationMaxLength() {
      return this.$constants.findings.situationMaxLength
    },

    siteId() {
      return this.routine.site?._id ?? this.routine.site
    },

    siteIds() {
      return [this.siteId]
    },
  },

  methods: {
    async fetchRoutineDefinition() {
      if (!_.isEmpty(this.routineDefinition)) {
        this.presetSituationTypes = this.routineDefinition.presetSituationTypes ?? []
        this.presetTags = this.routineDefinition.presetTags ?? []
        this.availableTagsForFindings =
          this.routineDefinition.availableTagsForFindings ?? []

        this.filterSelectedTagsByAvailability()

        return
      }

      const response = await api.routine.getById(
        this.organizationId,
        this.routine.routineDefinition
      )

      this.presetSituationTypes = response.data.presetSituationTypes ?? []
      this.presetTags = response.data.presetTags ?? []
      this.availableTagsForFindings =
        response.data.availableTagsForFindings ?? []

      this.filterSelectedTagsByAvailability()
    },

    getAvailableFindingTagIds() {
      return (
        this.resolvedAvailableTagsForFindings?.map?.(tag =>
          utils.getEntityId(tag)
        ) ?? []
      )
    },

    filterSelectedTagsByAvailability() {
      const validTagIds = this.getAvailableFindingTagIds()
      if (_.isEmpty(validTagIds)) return

      this.finding.tags = this.finding.tags.filter(tagId =>
        validTagIds.includes(utils.getEntityId(tagId))
      )
    },

    handleTagsChange(value) {
      this.finding = { ...this.finding, ...value }
    },

    handleMediaChange(media) {
      this.finding.media = media
    },

    handleSetDueDate(date) {
      this.finding.dueDate = date
    },

    handleCancel() {
      this.$emit('close')
      this.availableAccounts = []
      this.resetFinding()
    },

    async handleSave() {
      const wasCreated = await this.createFinding()
      if (!wasCreated) return

      this.availableAccounts = []
      this.resetFinding()
    },

    resetFinding() {
      const selectedTagIds = this.selectedTagIds.map(tagId =>
        utils.getEntityId(tagId)
      )
      const validTagIds = this.getAvailableFindingTagIds()
      const initialTags = _.isEmpty(validTagIds)
        ? selectedTagIds
        : selectedTagIds.filter(tagId => validTagIds.includes(tagId))

      this.finding = {
        situation: '',
        categories: [],
        tags: initialTags,
        situationTypes: [],
        assignees: [],
        dueDate: null,
        media: [],
        revType: this.defaultRevType,
      }
    },

    async createFinding() {
      if (this.isFindingTagsRequired && _.isEmpty(this.finding.tags)) {
        this.showError(this.$t('routines.finding_tags_required'))
        return false
      }

      try {
        this.isLoading = true

        const response = await api.findings.create({
          organizationId: this.organizationId,
          data: {
            ...this.finding,
            media: this.finding?.media?.map?.(media => media.id) ?? [],
            assignees: this.finding?.assignees ?? [],
            situation: this.finding?.situation ?? this.situation,
            routine: this.routine.id,
            site: utils.getEntityId(this.routine.site),
            revType: this.finding?.revType ?? this.defaultRevType,
            tags: this.finding.tags,
          },
        })
        this.trackCreateFinding({
          situation: this.finding?.situation ?? this.situation,
          hasMedia: Boolean(this.finding?.media?.length),
          hasAssignees: Boolean(this.finding?.assignees?.length),
          numberOfAssignees: this.finding?.assignees?.length ?? 0,
          revType: this.finding?.revType ?? this.defaultRevType,
          hasDueDate: Boolean(this.finding?.dueDate),
        })
        this.$emit('created', response.data)
        return true
      } catch (err) {
        this.handleRequestError(err)
        return false
      } finally {
        this.isLoading = false
      }
    },
  },
}
</script>
